import React, { useContext, useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useErrorHandler } from 'react-error-boundary';
import queryString from 'query-string';

import { EVENTS, MarketplaceSearchType } from 'constants/events';
import { FeatureFlag } from 'constants/featureFlag';
import { FEATURED_SECTIONS } from 'constants/products';
import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import useInfiniteQueryAlgoliaProducts from 'hooks/useInfiniteQueryAlgoliaProducts';
import useQueryRelatedSearchProducts from 'hooks/useQueryRelatedSearchProducts';
import useSemanticProductSearch from 'hooks/useSemanticProductSearch';
import {
  getProductsQueryParams,
  getFeaturedSectionFilters,
  removeDuplicateProducts,
} from 'utils/algolia';
import { getActiveEmployeeWallets } from 'utils/employee';
import { logEvent } from 'utils/amplitude';
import { AccountRoutes } from 'utils/routeGenerator';
import { logRudderstackEvent } from 'utils/rudderstack';
import { marketplaceEnabled } from 'utils/store';

import ProductSearchResults from './ProductSearchResults';

const ProductSearchResultsPage = () => {
  const location = useLocation();
  const { employee, company } = useContext(CurrentUserContext);
  const handleError = useErrorHandler();
  const featureFlag = useContext(FeatureFlagContext);

  const {
    q: query = '',
  }: {
    q?: string;
  } = queryString.parse(location.search);

  useEffect(() => {
    logEvent(EVENTS.QuerySearched, { search_query: query });
    logRudderstackEvent(EVENTS.QuerySearched, { search_query: query });
  }, [query]);

  const {
    settings: {
      employee_country_exchage_rate: employee_country_exchange_rate,
      country,
      currency,
    },
  } = employee;

  const employeeWallets = getActiveEmployeeWallets({
    company_wallet_configurations: company.company_wallet_configurations,
    employer_sponsored_wallets: employee.employee_wallets,
    store_enabled: true,
  });

  const queryParams = employeeWallets.map((employeeWallet) =>
    getProductsQueryParams({
      employee_wallet: employeeWallet,
      country,
    })
  );
  const {
    data,
    isLoading: isVendorsLoading,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQueryAlgoliaProducts({
    query_key: [`searchProducts`, { query, country }],
    query_params: queryParams,
    search_query: query,
    onError: (error: Error) => handleError(error),
  });

  const productPages = data?.pages;

  const conventionalSearchResults = useMemo(
    () =>
      productPages?.flatMap(({ products }) =>
        removeDuplicateProducts(products)
      ) ?? [],
    [productPages]
  );

  const finishedPaginatingResults =
    conventionalSearchResults.length > 0 && !hasNextPage;

  const hasEmptySearchResultsFlag = featureFlag.flag(
    FeatureFlag.EMPTY_SEARCH_RESULTS,
    false
  );

  const conventionalSearchIsFinishedAndEmpty =
    !isVendorsLoading && conventionalSearchResults.length === 0;

  const shouldUseSemanticSearchResults =
    conventionalSearchIsFinishedAndEmpty && hasEmptySearchResultsFlag;

  const {
    products: semanticSearchResults = [],
    isLoading: isSemanticSearchLoading,
  } = useSemanticProductSearch({
    query,
    enabled: hasEmptySearchResultsFlag,
    employee,
  });

  const searchResultLoading =
    conventionalSearchIsFinishedAndEmpty && hasEmptySearchResultsFlag
      ? isSemanticSearchLoading
      : isVendorsLoading;

  const searchResults = shouldUseSemanticSearchResults
    ? semanticSearchResults
    : conventionalSearchResults;

  const searchResultsTotal = shouldUseSemanticSearchResults
    ? semanticSearchResults.length
    : conventionalSearchResults.length;

  const { data: relatedProducts } = useQueryRelatedSearchProducts({
    country,
    employee_wallets: employeeWallets,
    onError: (error: Error) => handleError(error),
    products: searchResults || [],
    query,
    finished_paginating_results: finishedPaginatingResults,
  });

  const featuredSectionQueryParams = employeeWallets.map((employee_wallet) =>
    getProductsQueryParams({
      country,
      employee_wallet,
      ...getFeaturedSectionFilters({
        featured_section: FEATURED_SECTIONS.POPULAR,
      }),
    })
  );
  const { data: suggestedProductsData } = useInfiniteQueryAlgoliaProducts({
    query_key: FEATURED_SECTIONS.POPULAR,
    query_params: featuredSectionQueryParams,
    onError: (error: Error) => handleError(error),
  });
  const { pages = [] } = suggestedProductsData ?? {};
  const suggestedProducts = pages
    .flatMap(({ products }) => products)
    .slice(0, 9);

  if (!marketplaceEnabled(employee)) {
    return <Navigate replace to={AccountRoutes.list()} />;
  }

  const searchType = shouldUseSemanticSearchResults
    ? MarketplaceSearchType.MisoSemanticSearch
    : MarketplaceSearchType.AlgoliaSearch;

  return (
    <ProductSearchResults
      company={company}
      currency={currency}
      products={searchResults}
      employeeCountryExchangeRate={employee_country_exchange_rate}
      totalProducts={searchResultsTotal!}
      query={query}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      relatedProducts={relatedProducts}
      suggestedProducts={suggestedProducts}
      isVendorsLoading={searchResultLoading}
      isEmptySearchAlternative={shouldUseSemanticSearchResults}
      searchType={searchType}
    />
  );
};

export default ProductSearchResultsPage;
